// =============================================================================
// OELOX — Orders domain helpers (staff Order Desk)
// =============================================================================

export function formatOrderDisplayNumber(id: string): string {
  const compact = id.replace(/-/g, "");
  const num = parseInt(compact.slice(-4), 16) % 10000;
  return `#${num || compact.slice(0, 4)}`;
}

export type OrderDbStatus =
  | "draft"
  | "pending_cashier_approval"
  | "approved"
  | "in_kitchen"
  | "ready_for_dispatch"
  | "blasted_to_drivers"
  | "assigned"
  | "picked_up"
  | "out_for_delivery"
  | "delivered"
  | "completed"
  | "cancelled"
  | "rejected";

/** Staff-facing buckets — Pending → Kitchen → Ready → Driver → Done */
export type OrderDeskStep =
  | "pending"
  | "kitchen"
  | "ready"
  | "driver"
  | "done"
  | "cancelled";

export type OrderDeskFilter = OrderDeskStep | "all";

export type OrderChannel = "whatsapp" | "storefront" | "dine_in" | "phone";
export type OrderFulfillment = "delivery" | "pickup";
export type OrderPaymentMethod = "cod" | "pay_at_pickup" | "unpaid";
export type OrderPipelineStage =
  | "customer"
  | "ai_agent"
  | "pending"
  | "cashier"
  | "kitchen"
  | "ready"
  | "driver"
  | "delivered";

export const ORDER_DESK_STEPS: OrderDeskStep[] = [
  "pending",
  "kitchen",
  "ready",
  "driver",
  "done",
];

export const PREP_TIME_OPTIONS = [10, 15, 20, 30, 45, 60] as const;

export const ORDERS_PAGE_SIZE = 30;

const PENDING = new Set<OrderDbStatus>(["draft", "pending_cashier_approval"]);
const KITCHEN = new Set<OrderDbStatus>(["approved", "in_kitchen"]);
const READY = new Set<OrderDbStatus>(["ready_for_dispatch"]);
const DRIVER = new Set<OrderDbStatus>([
  "blasted_to_drivers",
  "assigned",
  "picked_up",
  // Legacy dead state — still surface under Driver so ops can recover
  "out_for_delivery",
]);
const DONE = new Set<OrderDbStatus>(["delivered", "completed"]);
const CANCELLED = new Set<OrderDbStatus>(["cancelled", "rejected"]);

export function statusToDeskStep(status: string): OrderDeskStep {
  const s = status as OrderDbStatus;
  if (PENDING.has(s)) return "pending";
  if (KITCHEN.has(s)) return "kitchen";
  if (READY.has(s)) return "ready";
  if (DRIVER.has(s)) return "driver";
  if (DONE.has(s)) return "done";
  if (CANCELLED.has(s)) return "cancelled";
  return "pending";
}

export function deskStepToStatuses(step: OrderDeskFilter): OrderDbStatus[] | null {
  if (step === "all") return null;
  switch (step) {
    case "pending":
      return [...PENDING];
    case "kitchen":
      return [...KITCHEN];
    case "ready":
      return [...READY];
    case "driver":
      return [...DRIVER];
    case "done":
      return [...DONE];
    case "cancelled":
      return [...CANCELLED];
    default:
      return null;
  }
}

export function statusToPipelineStage(status: string): OrderPipelineStage {
  const step = statusToDeskStep(status);
  switch (step) {
    case "pending":
      return "cashier";
    case "kitchen":
      return "kitchen";
    case "ready":
      return "ready";
    case "driver":
      return "driver";
    case "done":
      return "delivered";
    case "cancelled":
      return "cashier";
    default:
      return "pending";
  }
}

/** Primary CTA after viewing an order in a given step */
export type OrderPrimaryAction =
  | "accept"
  | "start_cooking"
  | "mark_ready"
  | "dispatch_driver"
  | "mark_done"
  | "none";

export function primaryActionForStep(
  step: OrderDeskStep,
  fulfillment: OrderFulfillment = "delivery"
): OrderPrimaryAction {
  switch (step) {
    case "pending":
      return "accept";
    case "kitchen":
      return "mark_ready";
    case "ready":
      // Delivery must assign/blast; pickup may complete without a driver
      return fulfillment === "pickup" ? "mark_done" : "dispatch_driver";
    case "driver":
      return "mark_done";
    default:
      return "none";
  }
}

export function nextStatusAfterAction(
  action: Exclude<OrderPrimaryAction, "none" | "dispatch_driver">,
  _fulfillment: OrderFulfillment = "delivery"
): OrderDbStatus {
  switch (action) {
    case "accept":
      return "in_kitchen";
    case "start_cooking":
      return "in_kitchen";
    case "mark_ready":
      return "ready_for_dispatch";
    case "mark_done":
      return "completed";
  }
}

export interface OrderLineRecord {
  id: string;
  order_id: string;
  menu_item_id: string;
  variation_id: string | null;
  quantity: number;
  unit_price: number;
  special_instructions: string | null;
  item_name: string | null;
  line_total: number | null;
}

export interface OrderRecord {
  id: string;
  tenant_id: string;
  displayNumber: string;
  business_type: string;
  channel: OrderChannel;
  status: OrderDbStatus;
  deskStep: OrderDeskStep;
  pipeline_stage: OrderPipelineStage;
  customer_id: string | null;
  customer_name: string;
  customer_phone: string | null;
  customer_address: string | null;
  delivery_notes: string | null;
  fulfillment_type: OrderFulfillment;
  payment_method: OrderPaymentMethod;
  payment_status: string;
  subtotal: number;
  tax: number;
  delivery_fee: number;
  total: number;
  currency: string;
  prep_minutes: number | null;
  driver_request_delay_minutes: number;
  assigned_driver_id: string | null;
  driver_status: "pending" | "accepted" | "picked_up" | "delivered" | "cancelled" | null;
  reject_reason: string | null;
  staff_note: string | null;
  accepted_at: string | null;
  ready_at: string | null;
  completed_at: string | null;
  /** Origin: staff | whatsapp_ai | storefront | marketplace_app | ... */
  source: string;
  order_version: number;
  item_count: number;
  created_at: string;
  updated_at: string;
  waitingMinutes: number;
  stalled: boolean;
}

export interface OrderDetailRecord extends OrderRecord {
  items: OrderLineRecord[];
}

export interface OrderDeskStats {
  pending: number;
  kitchen: number;
  ready: number;
  driver: number;
  done_today: number;
}

export interface CreateOrderLineInput {
  menu_item_id: string;
  item_name: string;
  quantity: number;
  unit_price: number;
  special_instructions?: string | null;
}

export interface CreateStaffOrderInput {
  customer_name: string;
  customer_phone?: string | null;
  customer_address?: string | null;
  customer_id?: string | null;
  channel?: OrderChannel;
  fulfillment_type: OrderFulfillment;
  payment_method: OrderPaymentMethod;
  delivery_fee?: number;
  delivery_notes?: string | null;
  staff_note?: string | null;
  currency?: string;
  items: CreateOrderLineInput[];
  /** If true, goes straight to Pending; if false, starts as draft */
  submitForApproval?: boolean;
  /** If true, skips pending → goes directly to kitchen (POS staff orders) */
  autoAccept?: boolean;
}

export function waitingMinutesSince(iso: string, now = Date.now()): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((now - then) / 60_000));
}

/** Amber stall: waiting > 10 min in Pending, or kitchen past prep_minutes */
export function isOrderStalled(order: {
  status: string;
  created_at: string;
  accepted_at?: string | null;
  prep_minutes?: number | null;
}): boolean {
  const step = statusToDeskStep(order.status);
  if (step === "pending") {
    return waitingMinutesSince(order.created_at) >= 10;
  }
  if (step === "kitchen" && order.accepted_at && order.prep_minutes) {
    return waitingMinutesSince(order.accepted_at) >= order.prep_minutes + 5;
  }
  return false;
}

export function mapOrderRow(
  row: Record<string, unknown>,
  itemCount = 0,
  now = Date.now()
): OrderRecord {
  const status = String(row.status ?? "pending_cashier_approval") as OrderDbStatus;
  const created_at = String(row.created_at ?? new Date().toISOString());
  const waitingMinutes = waitingMinutesSince(created_at, now);
  const mapped = {
    id: String(row.id),
    tenant_id: String(row.tenant_id),
    displayNumber: formatOrderDisplayNumber(String(row.id)),
    business_type: String(row.business_type ?? "restaurant"),
    channel: (row.channel as OrderChannel) || "phone",
    status,
    deskStep: statusToDeskStep(status),
    pipeline_stage:
      (row.pipeline_stage as OrderPipelineStage) || statusToPipelineStage(status),
    customer_id: (row.customer_id as string) || null,
    customer_name: String(row.customer_name ?? "Guest"),
    customer_phone: (row.customer_phone as string) || null,
    customer_address: (row.customer_address as string) || null,
    delivery_notes: (row.delivery_notes as string) || null,
    fulfillment_type: (row.fulfillment_type as OrderFulfillment) || "delivery",
    payment_method: (row.payment_method as OrderPaymentMethod) || "unpaid",
    payment_status: String(row.payment_status ?? "unpaid"),
    subtotal: Number(row.subtotal ?? 0),
    tax: Number(row.tax ?? 0),
    delivery_fee: Number(row.delivery_fee ?? 0),
    total: Number(row.total ?? 0),
    currency: String(row.currency ?? "USD"),
    prep_minutes: row.prep_minutes != null ? Number(row.prep_minutes) : null,
    driver_request_delay_minutes: Number(row.driver_request_delay_minutes ?? 0),
    assigned_driver_id: (row.assigned_driver_id as string) || null,
    driver_status: (row.driver_status as OrderRecord["driver_status"]) || null,
    reject_reason: (row.reject_reason as string) || null,
    staff_note: (row.staff_note as string) || null,
    accepted_at: (row.accepted_at as string) || null,
    ready_at: (row.ready_at as string) || null,
    completed_at: (row.completed_at as string) || null,
    source: String(row.source ?? "staff"),
    order_version: Number(row.order_version ?? 1),
    item_count: itemCount,
    created_at,
    updated_at: String(row.updated_at ?? created_at),
    waitingMinutes,
    stalled: false,
  };
  return {
    ...mapped,
    stalled: isOrderStalled(mapped),
  };
}

export function mapOrderLine(row: Record<string, unknown>): OrderLineRecord {
  return {
    id: String(row.id),
    order_id: String(row.order_id),
    menu_item_id: String(row.menu_item_id ?? ""),
    variation_id: (row.variation_id as string) || null,
    quantity: Number(row.quantity ?? 1),
    unit_price: Number(row.unit_price ?? 0),
    special_instructions: (row.special_instructions as string) || null,
    item_name: (row.item_name as string) || null,
    line_total:
      row.line_total != null
        ? Number(row.line_total)
        : Number(row.quantity ?? 1) * Number(row.unit_price ?? 0),
  };
}

export function computeOrderTotals(
  items: Array<{ quantity: number; unit_price: number }>,
  deliveryFee = 0
): { subtotal: number; delivery_fee: number; total: number } {
  const subtotal = items.reduce(
    (sum, item) => sum + Number(item.quantity) * Number(item.unit_price),
    0
  );
  const delivery_fee = Math.max(0, Number(deliveryFee) || 0);
  return {
    subtotal,
    delivery_fee,
    total: subtotal + delivery_fee,
  };
}

export function validateCreateStaffOrder(input: CreateStaffOrderInput): string | null {
  if (!input.customer_name?.trim()) return "orders.validation.customerName";
  if (!input.items?.length) return "orders.validation.items";
  if (input.fulfillment_type === "delivery" && !input.customer_address?.trim()) {
    return "orders.validation.address";
  }
  for (const item of input.items) {
    if (!item.menu_item_id || !item.item_name || item.quantity < 1) {
      return "orders.validation.items";
    }
  }
  return null;
}
